import { StyleProps } from '$/components/ui';
import DangerouslySetInnerHTML, {
  FilledMarkdown,
  usePropertyFilledLink,
} from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import Form, { FormOptionsContext } from '$/components/ui/Form';
import { ButtonT, useThemeContext } from '$/components/ui/ThemeContext';
import {
  staticWebinarDimensions,
  WebinarPlayerContext,
  WebinarPlayerUIState,
  withWebinarPlayerContext,
} from '$/components/WebinarPlayer/context';
import {
  InteractionsStreamContext,
  InteractionsStreamContextState,
} from '$/components/WebinarPlayer/InteractionsStream/context';
import { PlayerMode } from '$/components/WebinarPlayer/types';
import { MediaRefFragment } from '$/GraphQL/schema/schema';
import { withContextValue } from '$/hooks/withPartialContexts';
import { langDirClassname } from '$/shared/frontend/utils/i18n';
import { readableColorOnBackground } from '$/shared/frontend/utils/tinyColor';
import formatSeconds from '$/utils/formatSeconds';
import { logger } from '$/utils/logger';
import { withProperties } from '$/utils/type';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { t } from '@lingui/macro';
import { Emoji, EmojiData } from 'emoji-mart';
import { EmojiSet } from 'emoji-mart/dist-es/utils/shared-props';
import {
  motion,
  MotionValue,
  useMotionValue,
  useTransform,
  animate,
  ResolvedValues,
} from 'framer-motion';
import React, { Suspense, useContext } from 'react';
import { Label } from 'semantic-ui-react';
import tinycolor from 'tinycolor2';
import tw, { theme } from 'twin.macro';
import { InteractionDisplayState, InteractionInfo } from '../InteractionInfo';
import { useInteractionIconColor } from '$/hooks/useInteractionIconColor';
import { useImageSrcFromRef } from '$/hooks/useImageSrcFromRef';
import { CardContext, CardContextState, useCardContext } from './CardContext';
import { MediaRibbon } from './MediaRibbon';
import { CountDownTimer } from './CountDownTimer';

interface InteractionMessageCardProps {
  interaction: InteractionInfo;
  title: string;
  description?: string;
  cardBody?: JSX.Element | null;
}

interface CardEmoji {
  emoji: string | EmojiData;
  set?: EmojiSet;
  size?: number;
}

interface SubmitButtonProps {
  disabled?: boolean;
  children?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  dontCenter?: boolean;
  className?: string;
  backClassName?: string;
  reversed?: boolean;
  theme?: ButtonT;
}

type CardMainProps = {
  className?: string;
  nextInteraction?: InteractionInfo;
  cardDisplayState: InteractionDisplayState;
  measuredCardHeight: string;
  measuredCardHeightPx: number;
  headerHeight?: MotionValue<number>;
  bodyOpacity?: MotionValue<number>;
  removingInteractionHeight?: MotionValue<number>;
  attendeeFirstName?: string | null;
  snappedIdRef?: React.RefObject<string | undefined>;
  willBeRemoved: boolean;
  isExpired?: boolean;

  icardCompressedCardHeight: number;
  icardTitleHeightPx: number;
} & InteractionsStreamContextState &
  Omit<
    CardContextState,
    | 'attendeeName'
    | 'headerHeight'
    | 'bodyOpacity'
    | 'isEdit'
    | 'displayState'
    | 'hasMedia'
    | 'setHasMedia'
  >;

const CardMain = withContextValue<
  WebinarPlayerUIState,
  CardMainProps,
  InteractionsStreamContextState &
    Pick<
      CardMainProps,
      | 'cardDisplayState'
      | 'measuredCardHeight'
      | 'icardCompressedCardHeight'
      | 'icardTitleHeightPx'
      | 'attendeeFirstName'
      | 'measuredCardHeightPx'
      | 'removingInteractionHeight'
      | 'willBeRemoved'
      | 'isExpired'
    >
>(
  WebinarPlayerContext,
  (
    { playbackPosition, interactionHeights, mode, dimensions, attendee, willBeRemovedCards },
    { interaction }
  ) => {
    const { scrollToCard, removingInteractionHeight, setRemovingInteraction } =
      React.useContext(InteractionsStreamContext);
    return {
      willBeRemoved: Boolean(interaction.id && willBeRemovedCards[interaction.id]),
      scrollToCard,
      removingInteractionHeight,
      setRemovingInteraction,
      cardDisplayState: interaction.cardDisplayState(playbackPosition, mode),
      measuredCardHeight:
        interactionHeights[interaction.shortId] !== undefined
          ? `${interactionHeights[interaction.shortId]}px`
          : 'auto',
      measuredCardHeightPx: interactionHeights[interaction.shortId] ?? 0,
      icardCompressedCardHeight: dimensions.actionsPane.icard.compressed.cardHeight,
      icardTitleHeightPx: dimensions.actionsPane.icard.titleHeightPx,
      attendeeFirstName: attendee?.firstName,
      isExpired: interaction.isExpired(playbackPosition, mode),
    };
  },
  'CardMain'
)(
  withWebinarPlayerContext(
    [
      'animations',
      'mode',
      'ewebinar',
      'activeInteractionRef',
      'scrollToCardOrPosition',
      'setInteractionHeight',
      'transitions',
    ],
    'CardMain'
  )(
    ({
      children,
      className,

      willBeRemoved,
      scrollToCard,
      removingInteractionHeight,
      setRemovingInteraction,
      cardDisplayState,
      measuredCardHeight,
      measuredCardHeightPx,
      headerHeight,
      bodyOpacity,
      icardCompressedCardHeight,
      icardTitleHeightPx,
      attendeeFirstName,
      isExpired,
      snappedIdRef,
      nextInteraction,
      context: {
        animations,
        mode,
        ewebinar,
        scrollToCardOrPosition,
        setInteractionHeight,
        transitions,
      },
      interaction,
      inChat,
      isSnapped,
      noBody,
    }) => {
      const isEdit = mode === PlayerMode.edit;
      const beforeRemoveSnappedIdRef = React.useRef<string | undefined | null>(undefined);

      inChat = Boolean(inChat);
      icardTitleHeightPx = inChat ? 40 : icardTitleHeightPx;

      const media = interaction.cardTitleMedia();
      let imageMediaRef: MediaRefFragment | null | undefined;
      const mediaRibbon = Array.isArray(media) ? media : undefined;

      if (typeof (media as MediaRefFragment)?.url === 'string') {
        const mediaRef = media as MediaRefFragment;
        // In case the URL has any {} in it, we need to replace them with the actual values
        imageMediaRef = {
          ...mediaRef,
          imageUrl: usePropertyFilledLink(mediaRef.imageUrl),
        };
      }

      const dir = langDirClassname(interaction.ewebinar?.viewerRoomSettings.language);
      const [hasMedia, setHasMedia] = React.useState(
        (Boolean(imageMediaRef) || Boolean(mediaRibbon && mediaRibbon.length > 0)) && !inChat
      );
      React.useEffect(() => {
        setHasMedia(
          (Boolean(imageMediaRef) || Boolean(mediaRibbon && mediaRibbon.length > 0)) && !inChat
        );
      }, [imageMediaRef, mediaRibbon, inChat]);

      const { css: iconColorCss } = useInteractionIconColor(interaction, ewebinar, {
        inverted: hasMedia,
      });

      const etheme = useThemeContext();
      const refCard = React.useRef<HTMLDivElement>(null);
      const staticOne = useMotionValue(1);
      const staticHeaderHeight = useMotionValue(
        staticWebinarDimensions.actionsPane.icard.expanded.mediaTitleHeightPx
      );

      React.useLayoutEffect(() => {
        const newCardHeight = refCard.current?.clientHeight;
        setInteractionHeight(interaction.shortId, newCardHeight ?? 0, true);
      }, [interaction, setInteractionHeight]);

      React.useEffect(() => {
        if (isExpired || willBeRemoved) {
          beforeRemoveSnappedIdRef.current = snappedIdRef?.current;
        }
      }, [isExpired, willBeRemoved, snappedIdRef]);

      const onCardAnimationComplete = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (animation: any) => {
          // Small hack to see if the animation that finished was exit
          if (animation.transitionEnd?.visibility === 'hidden') {
            if (!nextInteraction) {
              logger.debug('ANIM', 'CARD exit - no card bellow - scroll up');
              scrollToCardOrPosition(interaction.playbackPosition);
            } else {
              logger.debug('ANIM', 'CARD exit');
              setRemovingInteraction(interaction);
              removingInteractionHeight.set(measuredCardHeightPx);
              const newCardHeight = refCard.current?.clientHeight;
              animate(removingInteractionHeight, 0, {
                type: 'tween',
                duration: 0.5,
                onComplete: () => {
                  setInteractionHeight(interaction.shortId, newCardHeight ?? 0, true);
                },
              });
              if (
                beforeRemoveSnappedIdRef.current !== interaction.id ||
                snappedIdRef?.current !== beforeRemoveSnappedIdRef.current
              ) {
                // scroll to another card before this one was removed
                return;
              }
              nextInteraction && animations.start(`icon.snap.${nextInteraction.id}`);
            }
          }
        },
        [
          animations,
          interaction,
          snappedIdRef,
          nextInteraction,
          setInteractionHeight,
          measuredCardHeightPx,
          scrollToCardOrPosition,
          setRemovingInteraction,
          removingInteractionHeight,
        ]
      );

      const onClick = React.useCallback((): void => {
        if (refCard.current?.clientHeight === icardCompressedCardHeight) {
          // Unsnapped
          logger.debug('scrollToCard from CardMain');
          scrollToCard(interaction, true);
        }
      }, [icardCompressedCardHeight, interaction, scrollToCard]);

      const width = staticWebinarDimensions.actionsPane.icard.widthRem * 14;
      const height = width * staticWebinarDimensions.actionsPane.icard.mediaTitleRatio;
      const titleImageUrl = useImageSrcFromRef(imageMediaRef, {
        w: width,
        h: height,
      });

      return (
        <CardContext.Provider
          value={{
            inChat: inChat,
            isSnapped: isSnapped,
            noBody: noBody,
            interaction,
            displayState: cardDisplayState,
            hasMedia,
            setHasMedia,
            headerHeight: headerHeight ?? staticHeaderHeight,
            bodyOpacity: bodyOpacity ?? staticOne,
            attendeeName: attendeeFirstName ?? t`attendee`,
            isEdit,
          }}
        >
          <motion.div
            ref={refCard}
            style={{
              minHeight: 'unset',
              maxHeight: '100%',
              cursor: 'default',
              transformOrigin: '10% right',
            }}
            transition={transitions['card.expand']}
            onAnimationComplete={onCardAnimationComplete}
            exit={
              (interaction.shouldRemoveAnsweredCard() || willBeRemoved || isExpired) && !isEdit
                ? {
                    opacity: 0,
                    scale: 0,
                    transitionEnd: { visibility: 'hidden' },
                    transition: transitions['card.exit'],
                  }
                : undefined
            }
            variants={{
              // For some reason if we use height with hasMedia cards
              // the animation is off.  It's probably too restrictive
              // but if we use maxheight for custom cards it doesn't spring
              [`card.expand.all`]: {
                cursor: 'default',
                minHeight: 'unset',
                maxHeight: measuredCardHeight,
                transitionEnd: {
                  maxHeight: 'unset',
                },
              },
              [`card.compress.${interaction.id}`]: {
                minHeight: `${icardCompressedCardHeight}px`,
                maxHeight: `${icardCompressedCardHeight}px`,
                cursor: 'pointer',
              },
              [`card.wait.${interaction.id}`]: {
                scale: 0,
              },
            }}
            onClick={onClick}
            className={`
          icard-main flex-col
          ${className}
          ${hasMedia && 'has-media'}
          ${inChat && 'in-chat'}
          ${dir === 'rtl' && 'rtl'}
        `}
            css={css`
              max-height: 100%;
              display: flex;
              flex-direction: column;
              box-sizing: border-box;
              font-size: 0.875rem;

              .icard {
                overflow: visible;
                border-radius: 1.5rem;
              }

              .icard-header {
                ${tw`rounded-t-2xl`}
                height: ${icardTitleHeightPx}px;
                padding-top: 0.5rem;
                padding-bottom: 0;
                border-bottom: 1px solid ${etheme.player.card.border};

                overflow: hidden;
                z-index: 10;
                color: ${theme`colors.color.cardTitle`.toString()};
                align-items: center;

                background-size: contain;
                background-position: top center;
                align-items: flex-end;

                .icon {
                  ${iconColorCss}
                  ${true /* DD: Use color for now */ || hasMedia || inChat
                    ? ''
                    : 'filter: brightness(0.1);'}
j
                  height: 1.75rem;
                  width: 1.75rem;
                  margin-right: 0;
                  margin-left: 0.75rem;
                  &.rtl {
                    margin-left: 0;
                    margin-right: 0.75rem;
                  }

                  .icard-title-row {
                    align-items: flex-end;
                  }
                }
              }

              .icard-body {
                z-index: 20;
                overflow: visible;
              }

              &.has-media {
                .icard-header {
                  align-items: start;
                  height: unset;
                  background-size: cover;
                  ${headerHeight
                    ? ''
                    : `padding-top: ${
                        staticWebinarDimensions.actionsPane.icard.mediaTitleRatio * 100
                      }% !important;`}
                  ${mediaRibbon && mediaRibbon.length > 0
                    ? ''
                    : `background-image: url(${titleImageUrl});`}
                  color: #fff;

                  .icard-title-row {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    min-height: 3rem;
                    z-index: 15;
                    align-items: center;
                  }
                }
              }
            `}
          >
            {children}
          </motion.div>
        </CardContext.Provider>
      );
    }
  )
);

type AttendeeCardProps = Pick<
  WebinarPlayerUIState,
  | 'actionsPaneState'
  | 'mode'
  | 'attendeeId'
  | 'ewebinar'
  | 'transitions'
  | 'isCollapsedEmbeddedPlayer'
> & {
  cardHeight: string | number;
  icardCompressedCardHeight: number;
  icardExpandedTitleHeigh: number;
  interaction: InteractionInfo;
  hasMedia: boolean;
  headerHeight: MotionValue<number>;

  className?: string;
  onClickHeader?: () => void;
  noDescription?: boolean;
};

const StandardCard = withContextValue<
  WebinarPlayerUIState,
  AttendeeCardProps,
  Omit<AttendeeCardProps, 'className' | 'onClickHeader' | 'noDescription'>
>(
  WebinarPlayerContext,
  ({
    interactionHeights,
    dimensions,
    actionsPaneState,
    mode,
    attendeeId,
    ewebinar,
    transitions,
    isCollapsedEmbeddedPlayer,
  }) => {
    const { interaction, hasMedia, headerHeight } = useCardContext();
    return {
      cardHeight: interactionHeights[interaction.shortId] || 'unset',
      icardCompressedCardHeight: dimensions.actionsPane.icard.compressed.cardHeight,
      icardExpandedTitleHeigh: dimensions.actionsPane.icard.expanded.titleHeight,
      isCollapsedEmbeddedPlayer,
      actionsPaneState,
      mode,
      attendeeId,
      ewebinar,
      interaction,
      hasMedia,
      headerHeight,
      transitions,
    };
  },
  'StandardCard'
)((props) => {
  const {
    onClickHeader,
    noDescription,
    className,
    children,

    cardHeight,
    icardExpandedTitleHeigh,
    icardCompressedCardHeight,
    actionsPaneState,
    mode,
    attendeeId,
    ewebinar,
    interaction,
    hasMedia,
    headerHeight,
    transitions,
    isCollapsedEmbeddedPlayer,
  } = props;

  const primaryColorGlare = tinycolor(ewebinar.primaryColor).toHsv();
  primaryColorGlare.s = 5;
  primaryColorGlare.v = 90;

  const CardBody = interaction.cardBodyComponent();

  const Icon = interaction.icon();
  const [cardDimensions, setCardDimensions] = React.useState([0, 0]);

  const rootRef = React.useCallback((node: HTMLElement | null) => {
    if (!node) return;
    setCardDimensions([node.clientWidth, node.clientHeight]);
  }, []);

  const media = interaction.cardTitleMedia();
  const mediaRibbon = Array.isArray(media) ? media : undefined;

  const titleString = interaction.title();
  const descriptionString = interaction.description();
  const interactionId = interaction.id;
  const dir = langDirClassname(interaction.ewebinar?.viewerRoomSettings.language);
  const title = React.useMemo(() => {
    return (
      <motion.div
        className='icard-title-row flex flex-col w-full'
        css={[
          hasMedia &&
            css`
              padding: 0.5rem 1rem;
            `,
        ]}
      >
        <div className='flex w-full justify-between items-center'>
          <div
            className='icard-title flex flex-col items-start text-md font-semibold z-10'
            css={css`
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              max-height: 2.9em;
              overflow: hidden;
              p {
                line-height: 1.5;
              }
            `}
          >
            <FilledMarkdown className='flex text-md' md={titleString} />
          </div>
          {Icon && (
            <Icon
              className={`icon ${dir === 'rtl' && 'rtl'}`}
              css={css`
                z-index: 10;
              `}
            />
          )}
        </div>
        {hasMedia && !noDescription && (
          <motion.div
            css={css`
              overflow: hidden;
              color: #fff;

              min-height: 0rem;
              max-height: 0rem;

              a {
                color: #fff;
              }
            `}
            transition={transitions['card.expand']}
            variants={{
              [`card.compress.${interactionId}`]: {
                minHeight: '2rem',
                maxHeight: '2rem',
              },
              [`card.expand.all`]: {
                minHeight: '0rem',
                maxHeight: '0rem',
              },
            }}
          >
            <Description>{descriptionString}</Description>
          </motion.div>
        )}
      </motion.div>
    );
  }, [
    Icon,
    descriptionString,
    dir,
    hasMedia,
    interactionId,
    noDescription,
    titleString,
    transitions,
  ]);

  const etheme = useThemeContext();

  return React.useMemo(() => {
    if (mode !== PlayerMode.edit && !interaction.isVisibleToAttendee()) {
      return null;
    }

    return (
      <div
        className={`StandardCard icard ${className}`}
        css={[
          tw`flex flex-col relative overflow-visible rounded-2xl`,
          css`
            border: 1px solid ${etheme.player.card.border};
            min-height: ${icardCompressedCardHeight}px;
            .icard-header {
              background: #fff;
            }
            .icard-body {
              background: #fff;
            }
          `,
        ]}
      >
        {!attendeeId &&
          interaction.fragment.details?.integration &&
          Object.keys(interaction.fragment.details?.integration).length > 0 && // Sometimes integration = {}
          !interaction.integration?.isConnected() && (
            <Label
              ribbon='right'
              color='red'
              className='card-label'
              css={css`
                ${tw`shadow-md`}
                position: absolute !important;
                z-index: 100;
                right: 0;
                top: 0;
                transform: translateX(calc(-100% - 1rem - 1px)) translateY(40%) !important;
                min-width: 8rem !important;
              `}
            >
              Disconnected
            </Label>
          )}

        <motion.div
          ref={rootRef}
          onClick={onClickHeader}
          className={`
          icard-header flex relative px-4 items-center
          ${onClickHeader && 'cursor-pointer'}
        `}
          transition={transitions['card.expand']}
          style={
            hasMedia && actionsPaneState === 'shown' && !isCollapsedEmbeddedPlayer
              ? {
                  paddingTop: headerHeight,
                }
              : undefined
          }
          variants={
            hasMedia
              ? {
                  [`card.expand.all`]: {
                    height: 'unset',
                    borderBottomRightRadius: '0rem',
                    borderBottomLeftRadius: '0rem',
                  },
                  [`card.compress.${interaction.id}`]: {
                    height: `${icardCompressedCardHeight}px`,
                    paddingTop: `${icardCompressedCardHeight}px`,
                    borderBottomRightRadius: '1.5rem',
                    borderBottomLeftRadius: '1.5rem',
                  },
                }
              : undefined
          }
        >
          <motion.div
            style={{
              height: `${icardExpandedTitleHeigh}px`,
            }}
            transition={transitions['card.expand']}
            variants={{
              [`card.expand.all`]: {
                height: `${icardExpandedTitleHeigh}px`,
              },
              [`card.compress.${interaction.id}`]: {
                height: '100%',
              },
            }}
            css={css`
              ${!hasMedia ? 'display: none !important;' : ''}
              position: absolute;
              background: rgba(0, 0, 0, 0.5);
              bottom: 0;
              right: 0;
              left: 0;
            `}
          />

          {title}

          <MediaRibbon media={mediaRibbon} width={cardDimensions[0]} height={cardDimensions[1]} />
        </motion.div>

        <Body>
          {interaction.details?.description && !noDescription && (
            <Description className='mb-1/2e'>{interaction.description()}</Description>
          )}

          <motion.div
            css={css`
              overflow: visible;
              margin-top: 0rem;
              max-height: auto;
              opacity: 1;
            `}
            transition={transitions['card.expand']}
            variants={{
              [`card.compress.${interaction.id}`]: {
                opacity: 0,
                marginTop: '0rem',
                maxHeight: '0px',
              },
              [`card.expand.all`]: {
                opacity: 1,
                marginTop: interaction.details?.description && !noDescription ? '1rem' : '0rem',
                maxHeight: `${cardHeight}px`,
                transitionEnd: {
                  // Need 100% because card height can change depending on focus / submission state
                  maxHeight: 'unset',
                },
              },
            }}
          >
            {children ? (
              children
            ) : (
              <Suspense>
                <CardBody />
              </Suspense>
            )}
          </motion.div>
        </Body>
      </div>
    );
  }, [
    CardBody,
    actionsPaneState,
    attendeeId,
    cardDimensions,
    cardHeight,
    children,
    className,
    etheme.player.card.border,
    hasMedia,
    headerHeight,
    icardCompressedCardHeight,
    isCollapsedEmbeddedPlayer,
    icardExpandedTitleHeigh,
    interaction,
    mediaRibbon,
    mode,
    noDescription,
    onClickHeader,
    rootRef,
    title,
    transitions,
  ]);
});

const Jumbotron: React.FC<React.PropsWithChildren<StyleProps>> = ({ children, className }) => {
  return (
    <div
      css={css`
        ${tw`mb-2 text-lg leading-5`}
        line-height: 1.2rem;
      `}
      className={className}
    >
      {children}
    </div>
  );
};

const SubmitBubble = styled<React.FC<React.PropsWithChildren<StyleProps>>>(
  ({ className, children }) => {
    return <div className={className}>{children}</div>;
  }
)`
  ${tw`flex flex-col justify-center rounded-card pt-1e text-center items-center`}
`;

const IconThanks = styled<React.FC<StyleProps>>(({ className }) => {
  return <span className={className}>🙏</span>;
})`
  ${tw`mb-2 text-xl`}
`;

const Footer = styled<React.FC<React.PropsWithChildren<StyleProps>>>(({ children, className }) => {
  return <div className={className}>{children}</div>;
})`
  ${tw`flex items-center justify-center px-2 leading-5 pt-1/4e text-md font-semibold`}
`;

const Title = styled<React.FC<React.PropsWithChildren<StyleProps>>>(({ children, className }) => {
  return <div className={className}>{children}</div>;
})`
  ${tw`w-full mb-4`}
`;

const Subtitle: React.FC<{ className?: string; children: React.ReactNode }> = ({
  children,
  className,
}) => {
  const subtitle = children as string;

  return <FilledMarkdown className={`mb-1e text-md ${className}`} md={subtitle} />;
};

const Description: React.FC<{ className?: string; children: React.ReactNode }> =
  withWebinarPlayerContext(['transitions'])(({ children, context: { transitions }, className }) => {
    const { interaction } = useCardContext();
    const description = children as string;

    return (
      <motion.div
        className={`
        icard-description
        text-md
        ${className}
      `}
        css={css`
          display: -webkit-box;
          -webkit-box-orient: vertical;
          overflow: hidden;
        `}
        style={{
          maxHeight: '100%',
        }}
        transition={transitions['card.expand']}
        variants={{
          [`card.compress.${interaction.id}`]: {
            maxHeight: '1.4rem',
          },
          [`card.expand.all`]: {
            maxHeight: '100%',
          },
        }}
      >
        <FilledMarkdown className={`${className}`} md={description} />
      </motion.div>
    );
  });

const Body: React.FC<React.PropsWithChildren<StyleProps>> = withWebinarPlayerContext([
  'transitions',
])(({ children, context: { transitions }, className }) => {
  const { interaction, hasMedia } = useCardContext();

  /*
    DD: If we make the body scrollable on mobile it seems to break drag on the set of cards

  const { dimensions, mode } = context;
  const isEdit = mode === PlayerMode.edit;

  const maxHeightCardBody = hasMedia
    ? dimensions.actionsPane.heightPx - dimensions.actionsPane.icard.expanded.mediaTitleHeightPx
    : dimensions.actionsPane.heightPx - dimensions.actionsPane.icard.titleHeightPx;
  */

  return (
    <motion.div
      className={`
        icard-body markdown-content
        ${hasMedia ? 'has-media' : ''}
        ${className}
      `}
      css={[
        tw`text-sm leading-tight border-t-0 text-color-cardText overflow-hidden`,
        css`
          border-radius: 1.5rem;
          background: #fff;
          padding: 1rem;
          min-height: 2.5rem;

          border-top-left-radius: 0;
          border-top-right-radius: 0;

          .ui {
            &.radio {
              &.checkbox {
                min-height: 1.15rem !important;
                input {
                  &:checked {
                    ~ label {
                      &::after {
                        ${tw`bg-color-card-inner`}
                      }
                    }
                  }
                  ~ label {
                    &::before {
                      ${tw`border-color-card-outter`}
                    }
                    &::after {
                      ${tw`border-color-card-inner bg-color-card-inner`}
                    }
                  }
                }
              }
            }
          }
        `,
      ]}
      transition={transitions['card.expand']}
      transformValues={<V extends ResolvedValues>(v: V): V => {
        if (!v.paddingTop) {
          (v as unknown as { paddingTop: string }).paddingTop = '1rem';
        }
        return v;
      }}
      variants={
        hasMedia
          ? {
              [`card.compress.${interaction.id}`]: {
                opacity: 0,
              },
              [`card.expand.all`]: {
                opacity: 1,
              },
            }
          : {
              [`card.compress.${interaction.id}`]: {
                paddingTop: '0rem',
                overflow: 'hidden',
                maxHeight: 0,
              },
              [`card.expand.all`]: {
                paddingTop: '1rem',
                maxHeight: 9999,
                transitionEnd: {
                  maxHeight: 'unset',
                  height: 'unset',
                  overflow: 'visible',
                },
              },
            }
      }
    >
      {children}
    </motion.div>
  );
});

const EmojiIcon: React.FunctionComponent<CardEmoji> = ({ emoji, size = 25 }) => {
  return <Emoji emoji={emoji} set='apple' size={size} />;
};

const SubmitButton: React.FC<SubmitButtonProps> = withWebinarPlayerContext([
  //
  'ewebinar',
])((props) => {
  const {
    className,
    backClassName,
    children,
    theme,
    reversed,
    context: { ewebinar },
    disabled,
    onClick,
  } = props;

  const bgColor = ewebinar?.actionColor || '#39A1B2';
  const color = readableColorOnBackground(bgColor);
  const bgColorHover = tinycolor(reversed ? color : bgColor).darken(0.2);

  return (
    <div className={`flex w-full justify-end text-md ${backClassName}`}>
      <Form.SubmitButton
        className={className}
        css={css`
          font-family: '${ewebinar.font ?? 'unset'}';
          min-width: 40%;
          padding: 0 2rem;
          border-radius: ${ewebinar.borderRadius}px;
        `}
        theme={
          theme ?? {
            fg: reversed ? bgColor : color,
            fgHover: reversed ? bgColor : color,
            bg: reversed ? color : bgColor,
            bgHover: bgColorHover.toHex8String(),
          }
        }
        disabled={disabled}
        onClick={onClick}
      >
        <DangerouslySetInnerHTML markdownString={children} />
      </Form.SubmitButton>
    </div>
  );
});

interface ExpiresButtonProps {
  interaction: InteractionInfo;
  disabled?: boolean;
  button?: JSX.Element;
  timerClassName?: string;
  className?: string;
  expiredText?: string;
  buttonText?: string;
}

const ExpiresButton: React.FC<ExpiresButtonProps> = withWebinarPlayerContext(
  ['mode', 'playbackPositionValue', 'interactionInEdit'],
  'ExpiresButton'
)((props) => {
  const {
    interaction,
    disabled,
    button,
    className,
    timerClassName,
    expiredText,
    buttonText,
    context: webinarPlayerContext,
  } = props;
  const { mode, playbackPositionValue, interactionInEdit } = webinarPlayerContext;

  const { onSubmit } = useContext(FormOptionsContext);
  const endsIn = interaction.details?.endsIn;
  const hasTimer = interaction.showTimer(mode);

  const [isExpired, setIsExpired] = React.useState(false);
  const timeStringRef = React.useRef<HTMLDivElement>(null);

  const isEditingModal = interactionInEdit?.id === interaction.id;

  useTransform(playbackPositionValue, (pos) => {
    const node = timeStringRef.current;
    if (!hasTimer || !endsIn || !node) {
      return;
    }

    if (!isEditingModal && interaction.isExpired(pos, mode)) {
      node.innerText = expiredText ?? t`Offer expired`;
      if (!isExpired) setIsExpired(true);
      return;
    }

    if (isExpired) setIsExpired(false);
    node.innerText = t`Expires in ${formatSeconds(isEditingModal ? endsIn : interaction.timeLeft(pos), true)}`;
  });

  if (!interaction.details) return null;

  return (
    <div
      className={`flex flex-row text-center items-center w-full ${className}`}
      css={css`
        padding-top: 0.5rem;
        justify-content: ${hasTimer ? 'space-between' : 'flex-end'};
        ${hasTimer && `.submit-button { width: 100%; }`}
      `}
    >
      <CountDownTimer
        className={`${timerClassName ?? ''} ${
          !hasTimer && 'hidden'
        } text-left whitespace-normal w-full`}
      >
        <motion.div ref={timeStringRef}></motion.div>
      </CountDownTimer>

      {button ?? (
        <SubmitButton
          className='mt-0'
          dontCenter
          onClick={(e): void => {
            e.preventDefault();
            e.stopPropagation();
            onSubmit();
          }}
          disabled={isExpired || disabled}
        >
          {interaction.details.buttonText || buttonText || t`Claim`}
        </SubmitButton>
      )}
    </div>
  );
});

const InteractionMessageCard: React.FC<InteractionMessageCardProps> = ({
  interaction,
  cardBody,
}) => {
  return (
    <CardMain
      inChat
      interaction={interaction}
      css={css`
        ${tw`flex w-full p-0 mb-0`}
        width: 80%;
        max-width: 80%;

        .StandardCard {
          border-radius: 1.5rem;
          filter: drop-shadow(0px 2px 2px #d0d0d0);
          transform: translateZ(0);

          .icard-header {
            .icard-title {
              ${tw`text-sm!`}
              p {
                display: -webkit-box !important;
                -webkit-line-clamp: 1;
                -webkit-box-orient: vertical;
                line-break: anywhere;
                overflow: hidden;
              }
            }
            align-items: start;
          }
          .icard-body {
            ${tw`w-full py-1/2e!`}
            p {
              display: inline-block;
            }
            .icard-description {
              ${tw`text-sm!`}
            }
          }
          .icard-header,
          .icard-body {
            border: 1px solid ${theme`colors.chat.bubble`};
            background-color: ${theme`colors.chat.bubble`};
            color: #fff !important;

            .icon,
            a {
              color: #fff !important;
              --fa-secondary-color: #fff;
            }
          }
        }
      `}
    >
      <StandardCard>
        <div className='inline-flex items-center text-md font-semibold pb-1/2e'>{cardBody}</div>
      </StandardCard>
    </CardMain>
  );
};

export default withProperties(CardMain, {
  Footer,
  StandardCard,
  Title,
  Subtitle,
  Description,
  Body,
  SubmitBubble,
  SubmitButton,
  ExpiresButton,
  Jumbotron,
  IconThanks,
  Emoji: EmojiIcon,
  InteractionMessageCard,
});
